import React, { Suspense } from 'react'
import { Route, Navigate } from 'react-router-dom'
import { PageErrorBoundary, RouteLoader } from '@appic/ui-components'
import ProtectedRoute from '@/lib/auth/ProtectedRoute'
import { usePermissions } from '@appic/hooks'

const Wochenplanung = React.lazy(() => import('@/pages/scout/Wochenplanung'))
const Tagesplanung = React.lazy(() => import('@/pages/scout/Tagesplanung'))
const Praeferenzen = React.lazy(() => import('@/pages/scout/Praeferenzen'))
const MissionDetail = React.lazy(() => import('@/pages/scout/MissionDetail'))
const Unternehmenssuche = React.lazy(() => import('@/pages/scout/Unternehmenssuche'))
const MissionControl = React.lazy(() => import('@/pages/scout/MissionControl'))
const Scorecard = React.lazy(() => import('@/pages/scout/Scorecard'))
const Hilfe = React.lazy(() => import('@/pages/scout/Hilfe'))
// The route element is the KEYED wrapper: one mount of the page per draft (lead + draft instance), so the router
// never reuses a mounted report — its submit key and answers are mount-lifetime — for another one.
const VisitReport = React.lazy(() => import('@/pages/scout/VisitReportKeyed'))
const VisitReportViewer = React.lazy(() => import('@/pages/scout/VisitReportViewer'))
const LeadSkipFlow = React.lazy(() => import('@/pages/scout/LeadSkipFlow'))
const CampaignCockpit = React.lazy(() => import('@/pages/scout/CampaignCockpit'))
const PreQualCalling = React.lazy(() => import('@/pages/scout/PreQualCalling'))
const CallbackQueue = React.lazy(() => import('@/pages/scout/CallbackQueue'))
const CallTest = React.lazy(() => import('@/pages/scout/CallTest'))
const DialerConfig = React.lazy(() => import('@/pages/scout/DialerConfig'))
const Timesheet = React.lazy(() => import('@/pages/scout/Timesheet'))
const SessionHistory = React.lazy(() => import('@/pages/scout/SessionHistory'))
const CustomerView = React.lazy(() => import('@/pages/scout/CustomerView'))
const InsightsDashboard = React.lazy(() => import('@/pages/scout/InsightsDashboard'))
const ScanOrder = React.lazy(() => import('@/pages/scout/ScanOrder'))
const ProductsInfo = React.lazy(() => import('@/pages/scout/ProductsInfo'))
const IndustriesInfo = React.lazy(() => import('@/pages/scout/IndustriesInfo'))
const Quiz = React.lazy(() => import('@/pages/scout/Quiz'))
const Customer360 = React.lazy(() => import('@/pages/scout/Customer360'))
const CompanyDrawerPreview = React.lazy(() => import('@/pages/scout/CompanyDrawerPreview'))
const Lead = React.lazy(() => import('@/pages/scout/Lead'))
const JokerTest = React.lazy(() => import('@/pages/scout/JokerTest'))
const ReorderPreview = React.lazy(() => import('@/pages/scout/ReorderPreview'))

/** T106: Field reps land on Tagesplanung (current day), managers on Wochenplanung */
function ScoutLanding() {
  const { visibilityScope } = usePermissions()
  if (visibilityScope('scout') === 'own') {
    const today = new Date()
    const dow = today.getDay()
    if (dow === 0 || dow === 6) {
      return <Navigate to="/scout/wochenplanung" replace />
    }
    return <Navigate to="/scout/tagesplanung" replace />
  }
  return <Navigate to="/scout/wochenplanung" replace />
}

function protectedRoute(path: string, label: string, Component: React.ComponentType) {
  return (
    <Route path={path} element={
      <PageErrorBoundary>
        <Suspense fallback={<RouteLoader label={label} />}>
          <ProtectedRoute><Component /></ProtectedRoute>
        </Suspense>
      </PageErrorBoundary>
    } />
  )
}

export function renderScoutRoutes(loadingSettingsLabel: string): React.ReactElement {
  return (
    <>
      <Route path="scout" element={<ProtectedRoute><ScoutLanding /></ProtectedRoute>} />
      {protectedRoute('scout/wochenplanung', 'Loading weekly planning...', Wochenplanung)}
      {protectedRoute('scout/tagesplanung', 'Loading daily planning...', Tagesplanung)}
      {protectedRoute('scout/praeferenzen', 'Loading preferences...', Praeferenzen)}
      {protectedRoute('scout/missions/:id', 'Loading mission detail...', MissionDetail)}
      {protectedRoute('scout/unternehmenssuche', 'Loading company search...', Unternehmenssuche)}
      <Route path="scout/preferences" element={
        <PageErrorBoundary>
          <Suspense fallback={<RouteLoader label={loadingSettingsLabel} />}>
            <ProtectedRoute>
              <Navigate to="/scout/mission-control?tab=preferences" replace />
            </ProtectedRoute>
          </Suspense>
        </PageErrorBoundary>
      } />
      {protectedRoute('scout/mission-control', 'Loading mission control...', MissionControl)}
      {protectedRoute('scout/scorecard', 'Loading scorecard...', Scorecard)}
      {protectedRoute('scout/visit-report/:leadId', 'Loading visit report...', VisitReport)}
      {protectedRoute('scout/visit-reports', 'Loading visit reports...', VisitReportViewer)}
      {protectedRoute('scout/skip-lead/:leadId', 'Loading...', LeadSkipFlow)}
      {protectedRoute('scout/lead/:id', 'Loading lead briefing...', Lead)}
      {protectedRoute('scout/prequal', 'Loading campaign...', CampaignCockpit)}
      <Route path="scout/prequal/calling" element={
        <ProtectedRoute>
          <Navigate to="/scout/calling" replace />
        </ProtectedRoute>
      } />
      {protectedRoute('scout/calling', 'Loading calling cockpit...', PreQualCalling)}
      {/* Deep-link: open the cockpit directly on ONE lead (durable, shareable,
          survives reload — unlike route-state). Permission is enforced by the SL
          calling-lead-card view (RLS/JWT-scoped): no access → empty → NEVER-DARK. */}
      {protectedRoute('scout/calling/:leadId', 'Loading lead...', PreQualCalling)}
      {protectedRoute('scout/reorder-preview', 'Reorder preview...', ReorderPreview)}
      {protectedRoute('scout/callback-queue', 'Loading callback queue...', CallbackQueue)}
      {protectedRoute('scout/quiz', 'Loading quiz...', Quiz)}
      {protectedRoute('scout/info/products', 'Loading products...', ProductsInfo)}
      {protectedRoute('scout/info/industries', 'Loading industries...', IndustriesInfo)}
      {protectedRoute('scout/call-test', 'Loading call test...', CallTest)}
      {protectedRoute('scout/joker-test', 'Loading joker test...', JokerTest)}
      {protectedRoute('scout/timesheet', 'Loading timesheet...', Timesheet)}
      {protectedRoute('scout/sessions', 'Loading sessions...', SessionHistory)}
      {protectedRoute('scout/dialer-config', 'Loading dialer config...', DialerConfig)}
      {protectedRoute('scout/customers', 'Loading customers...', CustomerView)}
      {protectedRoute('scout/insights', 'Loading insights...', InsightsDashboard)}
      {protectedRoute('scout/scan-order', 'Loading scan...', ScanOrder)}
      {protectedRoute('scout/hilfe', 'Loading help...', Hilfe)}
      {protectedRoute('scout/customer360', 'Loading customer 360...', Customer360)}
      {protectedRoute('scout/dev/company-drawer', 'Loading CompanyDrawer preview...', CompanyDrawerPreview)}
    </>
  )
}
